// The Admin page's Item bank tab (RFC-056 §1, D6): who Share lets into the
// exam server's item bank, as a bank admin or a bank user — the add-on
// exam-bank, its role in its limits. A Share admin gives either, until a
// day or for good, and ends it, asked first; Share's admins are the bank's
// admins without a grant. A bank admin gives and ends users on the bank's
// own Members page too.
import { useCallback, useEffect, useState } from "react";
import { isAddress } from "../address";
import { api, type Grant } from "../api";
import { t, whenDay } from "../i18n";
import { useConfirm } from "./Confirm";
import { FormDialog } from "./FormDialog";
import { RowMenu, type MenuItem } from "./RowMenu";
import { EmptyRows, Grouped, initialOf } from "./Rows";
import { endOfDay } from "./Admins";

const errText = (err: unknown) => (err instanceof Error ? err.message : String(err));

export type AdminBankApi = Pick<typeof api, "adminGrants" | "grant" | "revoke">;

const roleOf = (g: Grant) => (g.limits.role === "admin" ? "admin" : "user");

function BankGrantRow({ g, onRevoke }: { g: Grant; onRevoke: (g: Grant) => void }) {
  const sub = [g.name, g.granted_by && t("admin:adminBy", { email: g.granted_by }), g.expires_at && t("bank:membersUntil", { w: whenDay(g.expires_at) })].filter(Boolean);
  const menu: MenuItem[] = g.live ? [{ label: t("admin:adminRevoke"), onSelect: () => onRevoke(g), danger: true }] : [];
  return (
    <li className={`share-row admin-row ${g.live ? "is-live" : "is-ended"}`} data-testid="bank-grant">
      <span className="row-lead" />
      <span className="kind-box" aria-hidden="true">
        {initialOf(g.email)}
      </span>
      <div className="row-main">
        <span className="row-title">{g.email}</span>
        <div className="row-sub">{sub.join(" · ")}</div>
      </div>
      <div className="row-meta">
        <span className="chip">{roleOf(g) === "admin" ? t("bank:roleAdmin") : t("bank:roleUser")}</span>
      </div>
      <RowMenu label={t("shares:moreFor", { title: g.email })} items={menu} />
    </li>
  );
}

function BankGrantDialog({ onSubmit, onClose }: { onSubmit: (email: string, role: "admin" | "user", until: string) => Promise<void>; onClose: () => void }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"admin" | "user">("user");
  const [until, setUntil] = useState("");
  return (
    <FormDialog
      id="bank-grant-dialog"
      testid="bank-grant-form"
      title={t("admin:adminBankGrantTitle")}
      submitLabel={t("admin:adminBankGrant")}
      canSubmit={isAddress(email)}
      onSubmit={() => onSubmit(email.trim(), role, until)}
      onClose={onClose}
    >
      <label className="field">
        {t("admin:adminEmail")}
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} aria-label={t("admin:adminEmail")} />
      </label>
      <label className="field">
        {t("admin:adminBankRole")}
        <select value={role} onChange={(e) => setRole(e.target.value as "admin" | "user")} aria-label={t("admin:adminBankRole")}>
          <option value="user">{t("bank:roleUser")}</option>
          <option value="admin">{t("bank:roleAdmin")}</option>
        </select>
      </label>
      <label className="field">
        {t("bank:membersUntilLabel")}
        <input type="date" value={until} onChange={(e) => setUntil(e.target.value)} aria-label={t("bank:membersUntilLabel")} />
      </label>
    </FormDialog>
  );
}

export function AdminBank({ client = api, onCount }: { client?: AdminBankApi; onCount?: (n: number) => void }) {
  const [list, setList] = useState<Grant[] | null>(null);
  const [granting, setGranting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [ask, confirmDialog] = useConfirm();
  const reload = useCallback(async () => {
    try {
      const l = await client.adminGrants("exam-bank");
      setList(l);
      onCount?.(l.filter((g) => g.live).length);
    } catch (err) {
      setError(errText(err));
    }
  }, [client, onCount]);
  useEffect(() => {
    void reload();
  }, [reload]);

  const give = async (email: string, role: "admin" | "user", until: string) => {
    const end = endOfDay(until);
    const g = await client.grant({ email, addon: "exam-bank", limits: { role }, ...(end ? { expires_at: end } : {}) });
    setDone(t("admin:adminBankGranted", { email: g.email }));
    setError(null);
    setGranting(false);
    await reload();
  };
  const revoke = async (g: Grant) => {
    if (!(await ask(t("admin:adminRevokeAsk", { email: g.email }), { action: t("admin:adminRevoke"), danger: true }))) return;
    try {
      await client.revoke(g.user_id, "exam-bank");
      setError(null);
    } catch (err) {
      setError(errText(err));
    }
    await reload();
  };
  const all = list ?? [];
  return (
    <div data-testid="admin-bank">
      <div className="panel-head">
        <p className="muted small panel-lead">{t("admin:adminBankLead")}</p>
        <button
          className="primary"
          aria-haspopup="dialog"
          aria-expanded={granting}
          aria-controls={granting ? "bank-grant-dialog" : undefined}
          onClick={() => (setDone(null), setGranting(true))}
        >
          {t("admin:adminBankGrant")}
        </button>
      </div>
      {error && <p className="error">{t("common:error", { m: error })}</p>}
      {done && (
        <p className="ok-text" role="status">
          {done}
        </p>
      )}
      {list === null ? (
        !error && <p className="muted">{t("common:loading")}</p>
      ) : all.length === 0 ? (
        <EmptyRows text={t("admin:adminBankNone")} />
      ) : (
        <Grouped
          id="bank-grants"
          groups={[
            { title: t("admin:adminGrantsLive"), items: all.filter((g) => g.live) },
            { title: t("admin:adminGrantsEnded"), items: all.filter((g) => !g.live) },
          ]}
          row={(g) => <BankGrantRow key={g.user_id} g={g} onRevoke={(x) => void revoke(x)} />}
        />
      )}
      {granting && <BankGrantDialog onSubmit={give} onClose={() => setGranting(false)} />}
      {confirmDialog}
    </div>
  );
}
